import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { Amount } from "@/components/shared/Amount";
import { AHeader, ACard, ATable } from "@/components/shared/AdminKit";
import { StatusBadge } from "@/components/shared/Badges";
import { fmtDate } from "@/components/shared/DashKit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowUpRight, ArrowDownLeft } from "lucide-react";

const methodsMap = (tr) => ({ card: tr("sp.method.card", "Carte"), mobile_money: tr("sp.method.mm", "Mobile Money"), usdt: tr("sp.method.usdt", "USDT TRC20") });
const typesMap = (tr) => ({ DEPOSIT: tr("sp.type.deposit", "Dépôt"), ORDER: tr("sp.type.order", "Commande"), REFUND: tr("sp.type.refund", "Remboursement"), ADMIN_ADJUSTMENT: tr("sp.type.adjustment", "Ajustement"), ADJUSTMENT: tr("sp.type.adjustment", "Ajustement"), BONUS: tr("sp.type.bonus", "Bonus") });

export function AdminPayments() {
  const { t: tr } = useContent();
  const METHODS = methodsMap(tr);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("");
  useEffect(() => { setLoading(true); api.get(`/admin/payments${method ? `?method=${method}` : ""}`).then((r) => { setItems(r.data); setLoading(false); }); }, [method]);
  return (
    <div>
      <AHeader title={tr("sp.pay.title", "Paiements")} subtitle={tr("sp.pay.subtitle", "Tous les paiements de recharge.")} action={
        <Select value={method || "all"} onValueChange={(v) => setMethod(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="payment-method-filter" className="border-slate-800 bg-slate-900 text-white w-48"><SelectValue placeholder={tr("sp.method_ph", "Méthode")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{tr("sp.all_f", "Toutes")}</SelectItem>{Object.entries(METHODS).filter(([k]) => k !== "mobile_money" || true).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>} />
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <div className="py-10 text-center text-slate-400">{tr("sp.pay.empty", "Aucun paiement.")}</div>
          : (
            <ATable head={[tr("sp.col.id", "ID"), tr("sp.col.user", "Utilisateur"), tr("sp.col.method", "Méthode"), tr("sp.col.amount", "Montant"), tr("sp.col.status", "Statut"), tr("sp.col.ref", "Référence"), tr("sp.col.date", "Date")]}>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-slate-800/60 last:border-0" data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-3 font-mono text-xs">{p.id.slice(0, 8)}</td>
                  <td className="px-3 py-3 text-slate-400">{p.user_email}</td>
                  <td className="px-3 py-3">{METHODS[p.method]}</td>
                  <td className="px-3 py-3 font-medium"><Amount value={p.amount} /></td>
                  <td className="px-3 py-3"><StatusBadge status={p.status === "completed" ? "completed" : p.status} /></td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-400">{p.reference}</td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(p.created_at)}</td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
    </div>
  );
}

export function AdminTransactions() {
  const { t: tr } = useContent();
  const METHODS = methodsMap(tr);
  const TYPES = typesMap(tr);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  useEffect(() => { setLoading(true); api.get(`/admin/transactions${type ? `?type=${type}` : ""}`).then((r) => { setItems(r.data); setLoading(false); }); }, [type]);
  return (
    <div>
      <AHeader title={tr("sp.txn.title", "Transactions")} subtitle={tr("sp.txn.subtitle", "Registre financier complet.")} action={
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="admin-txn-type" className="border-slate-800 bg-slate-900 text-white w-48"><SelectValue placeholder={tr("sp.type_ph", "Type")} /></SelectTrigger>
          <SelectContent><SelectItem value="all">{tr("sp.all_m", "Tous")}</SelectItem>{[["DEPOSIT", TYPES.DEPOSIT], ["ORDER", TYPES.ORDER], ["REFUND", TYPES.REFUND], ["ADMIN_ADJUSTMENT", TYPES.ADMIN_ADJUSTMENT], ["BONUS", TYPES.BONUS]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>} />
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : (
            <ATable head={[tr("sp.col.date", "Date"), tr("sp.col.type", "Type"), tr("sp.col.amount", "Montant"), tr("sp.col.balance_after", "Solde après"), tr("sp.col.method", "Méthode"), tr("sp.col.ref", "Référence"), tr("sp.col.status", "Statut")]}>
              {items.map((t) => (
                <tr key={t.id} className="border-b border-slate-800/60 last:border-0" data-testid={`admin-txn-${t.id}`}>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-3"><span className="inline-flex items-center gap-1.5">{t.amount >= 0 ? <ArrowDownLeft className="h-3.5 w-3.5 text-emerald-400" /> : <ArrowUpRight className="h-3.5 w-3.5 text-red-400" />}{TYPES[t.type] || t.type}</span></td>
                  <td className={`px-3 py-3 font-medium ${t.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}><Amount value={t.amount} /></td>
                  <td className="px-3 py-3"><Amount value={t.balance_after} /></td>
                  <td className="px-3 py-3 text-slate-400">{METHODS[t.payment_method] || "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-400">{t.reference}</td>
                  <td className="px-3 py-3 capitalize text-slate-400">{t.status}</td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
    </div>
  );
}

export function AdminRefunds() {
  const { t: tr } = useContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/refunds").then((r) => { setItems(r.data); setLoading(false); }); }, []);
  return (
    <div>
      <AHeader title={tr("sp.ref.title", "Remboursements")} subtitle={tr("sp.ref.subtitle", "Historique des remboursements émis.")} />
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <div className="py-10 text-center text-slate-400">{tr("sp.ref.empty", "Aucun remboursement.")}</div>
          : (
            <ATable head={[tr("sp.col.order", "Commande"), tr("sp.col.amount", "Montant"), tr("sp.col.reason", "Raison"), tr("sp.col.status", "Statut"), tr("sp.col.date", "Date")]}>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-800/60 last:border-0" data-testid={`refund-row-${r.id}`}>
                  <td className="px-3 py-3 font-mono text-xs">#{r.order_number}</td>
                  <td className="px-3 py-3 font-medium text-emerald-400"><Amount value={r.amount} /></td>
                  <td className="px-3 py-3 text-slate-400">{r.reason}</td>
                  <td className="px-3 py-3"><StatusBadge status="completed" /></td>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(r.created_at)}</td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
    </div>
  );
}

export function AdminAuditLogs() {
  const { t: tr } = useContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => { setItems(r.data); setLoading(false); }); }, []);
  return (
    <div>
      <AHeader title={tr("sp.audit.title", "Journaux d'audit")} subtitle={tr("sp.audit.subtitle", "Traçabilité des actions administrateur.")} />
      <ACard>
        {loading ? <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div>
          : items.length === 0 ? <div className="py-10 text-center text-slate-400">{tr("sp.audit.empty", "Aucun journal.")}</div>
          : (
            <ATable head={[tr("sp.col.date", "Date"), tr("sp.col.user", "Utilisateur"), tr("sp.col.action", "Action"), tr("sp.col.entity", "Entité"), tr("sp.col.id", "ID"), tr("sp.col.ip", "IP")]}>
              {items.map((a) => (
                <tr key={a.id} className="border-b border-slate-800/60 last:border-0" data-testid={`audit-row-${a.id}`}>
                  <td className="px-3 py-3 text-slate-400">{fmtDate(a.created_at)}</td>
                  <td className="px-3 py-3 text-slate-400">{a.user_email || "—"}</td>
                  <td className="px-3 py-3"><span className="rounded bg-slate-800 px-2 py-0.5 font-mono text-xs">{a.action}</span></td>
                  <td className="px-3 py-3">{a.entity}</td>
                  <td className="px-3 py-3 font-mono text-xs text-slate-500">{a.entity_id?.slice(0, 8)}</td>
                  <td className="px-3 py-3 text-slate-500">{a.ip || "—"}</td>
                </tr>
              ))}
            </ATable>
          )}
      </ACard>
    </div>
  );
}
